/**
 * Serviço para gerenciar formulários pré-consulta
 * Permite que pacientes preencham informações antes da consulta
 */

import { database } from '../config/backend';
import { ref, get, set, push, query, orderByChild, equalTo, limitToLast } from './phpDataAdapter';

export interface PreConsultationForm {
  id?: string;
  patientId: string;
  nutritionistId: string;
  consultationId?: string; // ID da consulta relacionada (se já agendada)
  status: 'pending' | 'reviewed' | 'completed';
  submittedAt: string;
  reviewedAt?: string;
  
  // Dados do formulário
  personalInfo: {
    age?: number;
    gender?: 'male' | 'female' | 'other';
    height?: number; // cm
    weight?: number; // kg
    occupation?: string;
    phone?: string;
  };
  
  healthInfo: {
    medicalConditions?: string[];
    medications?: string[];
    allergies?: string[];
    surgeries?: string[];
    familyHistory?: string;
  };
  
  lifestyle: {
    activityLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
    exerciseFrequency?: number; // vezes por semana
    sleepHours?: number;
    waterIntake?: number; // litros por dia
    smoking?: boolean;
    alcohol?: 'never' | 'occasionally' | 'regularly';
  };
  
  dietaryHabits: {
    mealsPerDay?: number;
    eatingOutFrequency?: 'never' | 'sometimes' | 'often';
    cookingAtHome?: boolean;
    dietaryRestrictions?: string[]; // vegan, vegetarian, gluten-free, etc
    foodPreferences?: string;
    dislikes?: string[];
  };
  
  goals: {
    primaryGoal?: 'weight_loss' | 'weight_gain' | 'muscle_gain' | 'maintenance' | 'health' | 'performance';
    specificGoals?: string;
    timeline?: string;
    previousAttempts?: string;
  };
  
  additionalInfo?: string; // Campo livre para informações adicionais
}

/**
 * Cria um novo formulário pré-consulta
 */
export async function createPreConsultationForm(
  nutritionistId: string,
  patientId: string,
  consultationId?: string
): Promise<string> {
  const formRef = push(ref(database, 'preConsultations'));
  const formId = formRef.key!;
  
  const form: PreConsultationForm = {
    id: formId,
    patientId,
    nutritionistId,
    consultationId,
    status: 'pending',
    submittedAt: new Date().toISOString(),
    personalInfo: {},
    healthInfo: {},
    lifestyle: {},
    dietaryHabits: {},
    goals: {},
  };
  
  await set(formRef, form);
  return formId;
}

/**
 * Salva respostas do formulário pré-consulta
 */
export async function savePreConsultationForm(
  formId: string,
  formData: Partial<PreConsultationForm>
): Promise<void> {
  const formRef = ref(database, `preConsultations/${formId}`);
  
  // Manter campos existentes e atualizar apenas os fornecidos
  const currentForm = await get(formRef);
  const currentData = currentForm.exists() ? currentForm.val() : {};
  
  const updatedData = {
    ...currentData,
    ...formData,
    id: formId,
  };
  
  await set(formRef, updatedData);
}

/**
 * Submete formulário pré-consulta (marca como completo)
 */
export async function submitPreConsultationForm(formId: string): Promise<void> {
  const formRef = ref(database, `preConsultations/${formId}`);
  const form = await get(formRef);
  
  if (!form.exists()) {
    throw new Error('Formulário não encontrado');
  }
  
  const formData = form.val() as PreConsultationForm;
  
  await set(formRef, {
    ...formData,
    status: 'pending', // Aguardando revisão do nutricionista
    submittedAt: new Date().toISOString(),
  });
}

/**
 * Marca formulário como revisado pelo nutricionista
 */
export async function markPreConsultationAsReviewed(formId: string): Promise<void> {
  const formRef = ref(database, `preConsultations/${formId}`);
  const form = await get(formRef);
  
  if (!form.exists()) {
    throw new Error('Formulário não encontrado');
  }
  
  const formData = form.val() as PreConsultationForm;
  
  await set(formRef, {
    ...formData,
    status: 'reviewed',
    reviewedAt: new Date().toISOString(),
  });
}

/**
 * Busca formulários pré-consulta de um paciente
 */
export async function getPreConsultationFormsByPatient(
  patientId: string
): Promise<PreConsultationForm[]> {
  const formsRef = query(
    ref(database, 'preConsultations'),
    orderByChild('patientId'),
    equalTo(patientId)
  );
  
  const snapshot = await get(formsRef);
  
  if (!snapshot.exists()) {
    return [];
  }
  
  const forms = snapshot.val() as Record<string, PreConsultationForm>;
  return Object.entries(forms).map(([id, form]) => ({
    ...form,
    id,
  }));
}

/**
 * Busca formulários pré-consulta pendentes de um nutricionista
 */
export async function getPendingPreConsultationForms(
  nutritionistId: string
): Promise<PreConsultationForm[]> {
  const formsRef = ref(database, 'preConsultations');
  const snapshot = await get(formsRef);
  
  if (!snapshot.exists()) {
    return [];
  }
  
  const forms = snapshot.val() as Record<string, PreConsultationForm>;
  return Object.entries(forms)
    .map(([id, form]) => ({
      ...form,
      id,
    }))
    .filter(form => 
      form.nutritionistId === nutritionistId && 
      form.status === 'pending'
    )
    .sort((a, b) => 
      new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
    );
}

/**
 * Busca formulário pré-consulta por ID
 */
export async function getPreConsultationForm(
  formId: string
): Promise<PreConsultationForm | null> {
  const formRef = ref(database, `preConsultations/${formId}`);
  const snapshot = await get(formRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return {
    ...snapshot.val(),
    id: formId,
  } as PreConsultationForm;
}

/**
 * Busca formulário pré-consulta por consulta
 */
export async function getPreConsultationFormByConsultation(
  consultationId: string
): Promise<PreConsultationForm | null> {
  const formsRef = ref(database, 'preConsultations');
  const snapshot = await get(formsRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  const forms = snapshot.val() as Record<string, PreConsultationForm>;
  const form = Object.entries(forms).find(
    ([_, form]) => form.consultationId === consultationId
  );
  
  if (!form) {
    return null;
  }
  
  return {
    ...form[1],
    id: form[0],
  };
}

/**
 * Vincula formulário pré-consulta a uma consulta
 */
export async function linkPreConsultationToConsultation(
  formId: string,
  consultationId: string
): Promise<void> {
  const formRef = ref(database, `preConsultations/${formId}`);
  const form = await get(formRef);
  
  if (!form.exists()) {
    throw new Error('Formulário não encontrado');
  }
  
  const formData = form.val() as PreConsultationForm;
  
  await set(formRef, {
    ...formData,
    consultationId,
  });
}

